// Servicio de oportunidades de inversión
import { BaseService } from "../services/baseService";
import { logger, LOG_CATEGORIES } from "../utils/logger";

class OpportunitiesService extends BaseService {
    constructor() {
        super("opportunities", "opportunity");
    }

    // Helper function to log admin actions
    async logAdminAction(adminId, action, opportunityId, details = {}) {
        try {
            const { adminAuditService } = await import("../services/adminAuditService");
            await adminAuditService.logOpportunityAdminAction(adminId, action, opportunityId, {
                ...details,
                adminEmail: details.adminEmail || "unknown",
                ipAddress: "frontend",
                userAgent: navigator.userAgent,
                sessionId: "frontend_session"
            });
            // Logging administrativo local
            // LoggerService.log("admin", "admin-actions", {
            //     adminId,
            //     action,
            //     opportunityId,
            //     details,
            //     service: "opportunitiesService"
            // });
        } catch (error) {
            await logger.error(LOG_CATEGORIES.ADMIN, "Admin audit logging failed", error, { adminId, action, opportunityId });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "logAdminAction",
            //     adminId,
            //     action,
            //     opportunityId
            // });
        }
    }

    // Validate opportunity data
    validateOpportunityData(opportunityData) {
        const errors = [];
    
        // Required fields validation
        const requiredFields = ["title", "description", "amount", "location", "agentId"];
        for (const field of requiredFields) {
            if (!opportunityData[field]) {
                errors.push(`Missing required field: ${field}`);
            }
        }
    
        // Amount validation
        if (opportunityData.amount) {
            const amount = parseFloat(opportunityData.amount);
            if (isNaN(amount) || amount <= 0) {
                errors.push("Amount must be a positive number");
            } else if (amount < 1000) {
                errors.push("Minimum amount is €1,000");
            } else if (amount > 10000000) {
                errors.push("Maximum amount is €10,000,000");
            }
        }
    
        // Title validation
        if (opportunityData.title) {
            if (opportunityData.title.length < 10) {
                errors.push("Title must be at least 10 characters");
            } else if (opportunityData.title.length > 100) {
                errors.push("Title must be less than 100 characters");
            }
        }
    
        // Description validation
        if (opportunityData.description) {
            if (opportunityData.description.length < 50) {
                errors.push("Description must be at least 50 characters");
            } else if (opportunityData.description.length > 1000) {
                errors.push("Description must be less than 1000 characters");
            }
        }
    
        return errors;
    }

    // Get opportunities that need admin approval
    async getPendingApprovalOpportunities() {
        try {
            const opportunities = await this.getAll({ status: "pending_approval" });

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Pending approval opportunities retrieved", { 
                count: opportunities.length 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_PENDING_APPROVAL",
            //     count: opportunities.length,
            //     service: "opportunitiesService"
            // });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch pending opportunities", error);
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getPendingApprovalOpportunities"
            // });
            throw new Error(`Failed to fetch pending opportunities: ${error.message}`);
        }
    }

    // Get only approved/published opportunities for investors
    async getPublishedOpportunities() {
        try {
            const opportunities = await this.getAll({ 
                status: ["approved", "published", "active"] 
            });

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Published opportunities retrieved", { 
                count: opportunities.length 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_PUBLISHED",
            //     count: opportunities.length,
            //     service: "opportunitiesService"
            // });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch published opportunities", error);
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getPublishedOpportunities"
            // });
            throw new Error(`Failed to fetch published opportunities: ${error.message}`);
        }
    }

    async createInvestmentOpportunity(opportunityData) {
        try {
            const errors = this.validateOpportunityData(opportunityData);
            if (errors.length > 0) {
                throw new Error(`Validation failed: ${errors.join(", ")}`);
            }

            const opportunityId = this.generateId();
            const opportunityDoc = {
                ...opportunityData,
                status: "active",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            const result = await this.create(opportunityId, opportunityDoc);

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Investment opportunity created", { 
                opportunityId: result.id,
                agentId: opportunityData.agentId,
                amount: opportunityData.amount 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "changes", {
            //     action: "CREATE",
            //     entityType: "Opportunity",
            //     entityId: result.id,
            //     agentId: opportunityData.agentId,
            //     amount: opportunityData.amount,
            //     service: "opportunitiesService"
            // });

            return result.id;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to create investment opportunity", error, { 
                agentId: opportunityData.agentId 
            });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "createInvestmentOpportunity",
            //     agentId: opportunityData.agentId
            // });
            throw new Error(`Failed to create investment opportunity: ${error.message}`);
        }
    }

    async getActiveOpportunities() {
        try {
            const opportunities = await this.getAll(
                { status: ["active", "approved", "published"] },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Active opportunities retrieved", { 
                count: opportunities.length 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_ACTIVE",
            //     count: opportunities.length,
            //     service: "opportunitiesService"
            // });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch active opportunities", error);
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getActiveOpportunities"
            // });
            throw new Error(`Failed to fetch active opportunities: ${error.message}`);
        }
    }

    async getUserOpportunities(userId) {
        try {
            const opportunities = await this.getAll(
                { 
                    userId, 
                    status: ["active", "approved", "published"] 
                },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "User opportunities retrieved", { 
                userId, 
                count: opportunities.length 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_USER_OPPORTUNITIES",
            //     userId,
            //     count: opportunities.length,
            //     service: "opportunitiesService"
            // });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch user opportunities", error, { userId });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getUserOpportunities",
            //     userId
            // });
            throw new Error(`Failed to fetch user opportunities: ${error.message}`);
        }
    }

    async getFutureOpportunities() {
        try {
            const opportunities = await this.getAll(
                { status: "upcoming" },
                { orderBy: { field: "startDate", direction: "asc" } }
            );

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Future opportunities retrieved", { 
                count: opportunities.length 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_FUTURE",
            //     count: opportunities.length,
            //     service: "opportunitiesService"
            // });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch future opportunities", error);
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getFutureOpportunities"
            // });
            throw new Error(`Failed to fetch future opportunities: ${error.message}`);
        }
    }

    async getOpportunityById(opportunityId) {
        try {
            const opportunity = await this.getById(opportunityId);
            
            if (!opportunity) {
                await logger.warn(LOG_CATEGORIES.OPPORTUNITY, "Opportunity not found", { opportunityId });
                // Logging local para auditoría
                // LoggerService.log("opportunities", "access", {
                //     action: "GET_BY_ID_NOT_FOUND",
                //     entityType: "Opportunity",
                //     entityId: opportunityId,
                //     service: "opportunitiesService"
                // });
                return null;
            }

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Opportunity retrieved by ID", { opportunityId });
            // Logging local para auditoría
            // LoggerService.log("opportunities", "access", {
            //     action: "GET_BY_ID",
            //     entityType: "Opportunity",
            //     entityId: opportunityId,
            //     service: "opportunitiesService"
            // });

            return opportunity;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to get opportunity by ID", error, { opportunityId });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "getOpportunityById",
            //     opportunityId
            // });
            throw new Error(`Failed to get opportunity: ${error.message}`);
        }
    }

    async updateOpportunity(opportunityId, data, adminId = null) {
        try {
            const updateData = {
                ...data,
                updatedAt: new Date().toISOString()
            };

            const result = await this.update(opportunityId, updateData);

            // Log admin action if adminId is provided
            if (adminId) {
                await this.logAdminAction(adminId, "update_opportunity", opportunityId, {
                    changes: data,
                    adminEmail: "admin@capivio.com" // This should come from admin context
                });
            }

            // Log opportunity update for audit
            await this.logAuditEvent("updated", {
                opportunityId,
                adminId,
                changes: data
            });

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Opportunity updated", { 
                opportunityId, 
                adminId 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "changes", {
            //     action: "UPDATE",
            //     entityType: "Opportunity",
            //     entityId: opportunityId,
            //     adminId,
            //     changes: data,
            //     service: "opportunitiesService"
            // });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to update opportunity", error, { 
                opportunityId, 
                adminId 
            });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "updateOpportunity",
            //     opportunityId,
            //     adminId
            // });
            throw new Error(`Failed to update opportunity: ${error.message}`);
        }
    }

    async deleteOpportunity(opportunityId, adminId = null) {
        try {
            const result = await this.delete(opportunityId);

            // Log admin action if adminId is provided
            if (adminId) {
                await this.logAdminAction(adminId, "delete_opportunity", opportunityId, {
                    adminEmail: "admin@capivio.com" // This should come from admin context
                });
            }

            // Log opportunity deletion for audit
            await this.logAuditEvent("deleted", {
                opportunityId,
                adminId
            });

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Opportunity deleted", { 
                opportunityId, 
                adminId 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "changes", {
            //     action: "DELETE",
            //     entityType: "Opportunity",
            //     entityId: opportunityId,
            //     adminId,
            //     service: "opportunitiesService"
            // });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to delete opportunity", error, { 
                opportunityId, 
                adminId 
            });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "deleteOpportunity",
            //     opportunityId,
            //     adminId
            // });
            throw new Error(`Failed to delete opportunity: ${error.message}`);
        }
    }

    async createOpportunity(opportunityData, adminId = null) {
        try {
            const errors = this.validateOpportunityData(opportunityData);
            if (errors.length > 0) {
                throw new Error(`Validation failed: ${errors.join(", ")}`);
            }

            const opportunityId = this.generateId();
            const opportunityDoc = {
                ...opportunityData,
                status: "pending_approval",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            const result = await this.create(opportunityId, opportunityDoc);

            // Log admin action if adminId is provided
            if (adminId) {
                await this.logAdminAction(adminId, "create_opportunity", opportunityId, {
                    adminEmail: "admin@capivio.com" // This should come from admin context
                });
            }

            // Log opportunity creation for audit
            await this.logAuditEvent("created", {
                opportunityId: result.id,
                adminId,
                agentId: opportunityData.agentId,
                amount: opportunityData.amount
            });

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Opportunity created", { 
                opportunityId: result.id,
                adminId,
                agentId: opportunityData.agentId 
            });

            return result.id;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to create opportunity", error, { 
                adminId,
                agentId: opportunityData.agentId 
            });
            throw new Error(`Failed to create opportunity: ${error.message}`);
        }
    }

    async getOpportunitiesByAgentId(agentId) {
        try {
            const opportunities = await this.getAll(
                { agentId },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Agent opportunities retrieved", { 
                agentId, 
                count: opportunities.length 
            });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch agent opportunities", error, { agentId });
            throw new Error(`Failed to fetch agent opportunities: ${error.message}`);
        }
    }

    async getAllOpportunities() {
        try {
            const opportunities = await this.getAll({}, {
                orderBy: { field: "createdAt", direction: "desc" }
            });

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "All opportunities retrieved", { 
                count: opportunities.length 
            });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch all opportunities", error);
            throw new Error(`Failed to fetch all opportunities: ${error.message}`);
        }
    }

    // Get opportunities by status
    async getOpportunitiesByStatus(status) {
        try {
            const opportunities = await this.getAll(
                { status },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Opportunities by status retrieved", { 
                status, 
                count: opportunities.length 
            });

            return opportunities;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to fetch opportunities by status", error, { status });
            throw new Error(`Failed to fetch opportunities by status: ${error.message}`);
        }
    }

    // Approve opportunity
    async approveOpportunity(opportunityId, adminId, approvalData = {}) {
        try {
            const updateData = {
                status: "approved",
                approvedAt: new Date().toISOString(),
                approvedBy: adminId,
                approvalNotes: approvalData.notes,
                updatedAt: new Date().toISOString()
            };

            const result = await this.update(opportunityId, updateData);

            // Log admin action
            await this.logAdminAction(adminId, "approve_opportunity", opportunityId, {
                approvalNotes: approvalData.notes,
                adminEmail: "admin@capivio.com" // This should come from admin context
            });

            // Log approval for audit
            await this.logAuditEvent("approved", {
                opportunityId,
                adminId,
                approvalNotes: approvalData.notes
            });

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Opportunity approved", { 
                opportunityId, 
                adminId 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "changes", {
            //     action: "APPROVE",
            //     entityType: "Opportunity",
            //     entityId: opportunityId,
            //     adminId,
            //     approvalNotes: approvalData.notes,
            //     service: "opportunitiesService"
            // });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to approve opportunity", error, { 
                opportunityId, 
                adminId 
            });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "approveOpportunity",
            //     opportunityId,
            //     adminId
            // });
            throw new Error(`Failed to approve opportunity: ${error.message}`);
        }
    }

    // Reject opportunity
    async rejectOpportunity(opportunityId, adminId, rejectionData) {
        try {
            const updateData = {
                status: "rejected",
                rejectedAt: new Date().toISOString(),
                rejectedBy: adminId,
                rejectionReason: rejectionData.reason,
                rejectionNotes: rejectionData.notes,
                updatedAt: new Date().toISOString()
            };

            const result = await this.update(opportunityId, updateData);

            // Log admin action
            await this.logAdminAction(adminId, "reject_opportunity", opportunityId, {
                rejectionReason: rejectionData.reason,
                rejectionNotes: rejectionData.notes,
                adminEmail: "admin@capivio.com" // This should come from admin context
            });

            // Log rejection for audit
            await this.logAuditEvent("rejected", {
                opportunityId,
                adminId,
                rejectionReason: rejectionData.reason
            });

            await logger.info(LOG_CATEGORIES.OPPORTUNITY, "Opportunity rejected", { 
                opportunityId, 
                adminId,
                reason: rejectionData.reason 
            });

            // Logging local para auditoría
            // LoggerService.log("opportunities", "changes", {
            //     action: "REJECT",
            //     entityType: "Opportunity",
            //     entityId: opportunityId,
            //     adminId,
            //     rejectionReason: rejectionData.reason,
            //     rejectionNotes: rejectionData.notes,
            //     service: "opportunitiesService"
            // });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to reject opportunity", error, { 
                opportunityId, 
                adminId 
            });
            // Logging de error local
            // LoggerService.log("system", "error", {
            //     error: error.message,
            //     stack: error.stack,
            //     service: "opportunitiesService",
            //     context: "rejectOpportunity",
            //     opportunityId,
            //     adminId
            // });
            throw new Error(`Failed to reject opportunity: ${error.message}`);
        }
    }

    // Get opportunity statistics
    async getOpportunityStatistics() {
        try {
            const allOpportunities = await this.getAll();
            
            const statistics = {
                total: allOpportunities.length,
                pending: allOpportunities.filter(opp => opp.status === "pending_approval").length,
                approved: allOpportunities.filter(opp => opp.status === "approved").length,
                active: allOpportunities.filter(opp => opp.status === "active").length,
                published: allOpportunities.filter(opp => opp.status === "published").length,
                rejected: allOpportunities.filter(opp => opp.status === "rejected").length,
                totalAmount: allOpportunities.reduce((sum, opp) => sum + (opp.amount || 0), 0),
                averageAmount: allOpportunities.length > 0 ? 
                    allOpportunities.reduce((sum, opp) => sum + (opp.amount || 0), 0) / allOpportunities.length : 0
            };

            await logger.debug(LOG_CATEGORIES.OPPORTUNITY, "Opportunity statistics retrieved", statistics);

            return statistics;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.OPPORTUNITY, "Failed to get opportunity statistics", error);
            throw new Error("Failed to get opportunity statistics");
        }
    }
}

export const opportunitiesService = new OpportunitiesService(); 